// src/pages/ELearning/ComparativeAnalyticsPage.tsx — ФИЧА F
import React, { useEffect, useState } from 'react';
import { getComparativeAnalytics } from '../../api/client';
import { Loader2, TrendingUp, Users, BarChart3, Award } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#667eea', '#11998e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'];

const ComparativeAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await getComparativeAnalytics(period);
        setData(d);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [period]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} /></div>;
  if (!data) return <p>Нет данных</p>;

  const totals = data.totals || {};
  const byDept = data.byDepartment || [];
  const byRole = data.byRole || [];
  const timeline = data.timeline || [];
  const topUsers = data.topUsers || [];

  return (
    <>
      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } } .ca-card { animation: fadeUp 0.5s ease-out backwards; }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, animation: 'fadeUp 0.4s ease-out' }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(245,87,108,0.4)' }}>
          <BarChart3 size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' }}>
            Сравнительная аналитика
          </h1>
          <p style={{ color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>По отделам, ролям и сотрудникам</p>
        </div>
        <select value={period} onChange={e => setPeriod(e.target.value as any)} style={{ padding: '10px 16px', borderRadius: 14, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <option value="week">За неделю</option>
          <option value="month">За месяц</option>
          <option value="quarter">За квартал</option>
          <option value="year">За год</option>
        </select>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KPICard icon={<Award size={22} color="#667eea" />} value={totals.totalResults || 0} label="Результатов" delay={0} />
        <KPICard icon={<Users size={22} color="#11998e" />} value={totals.totalUsers || 0} label="Сотрудников" delay={0.05} />
        <KPICard icon={<TrendingUp size={22} color="#f59e0b" />} value={`${totals.avgScore || 0}%`} label="Средний балл" delay={0.1} />
        <KPICard icon={<BarChart3 size={22} color="#8b5cf6" />} value={`${totals.passRate || 0}%`} label="Пройдено" delay={0.15} />
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📈 Динамика по неделям</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="date" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" name="Пройдено" stroke="#667eea" strokeWidth={3} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="avgScore" name="Ср. балл %" stroke="#11998e" strokeWidth={3} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* По отделам */}
      {byDept.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🏢 По отделам</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byDept}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
              <XAxis dataKey="name" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" name="Пройдено" fill="#667eea" radius={[8, 8, 0, 0]} />
              <Bar dataKey="avgScore" name="Ср. балл" fill="#11998e" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* По ролям — Pie */}
      {byRole.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🎭 По ролям</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={byRole} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={(e: any) => `${e.name}: ${e.count}`}>
                {byRole.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Топ пользователей */}
      {topUsers.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🏆 Топ-10 сотрудников</h3>
          {topUsers.map((u: any, i: number) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 12, marginBottom: 6, background: i < 3 ? 'rgba(246,211,101,0.08)' : 'rgba(0,0,0,0.02)' }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: i < 3 ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' : 'rgba(102,126,234,0.15)', color: i < 3 ? '#fff' : '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                {i + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: '#8b8fa8' }}>{u.department} • {u.email}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#11998e' }}>{u.avgScore}%</div>
                <div style={{ fontSize: 10, color: '#8b8fa8' }}>{u.passed}/{u.count} пройдено</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const KPICard: React.FC<{ icon: React.ReactNode; value: any; label: string; delay: number }> = ({ icon, value, label, delay }) => (
  <div className="ca-card" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 22, padding: 20, boxShadow: '0 8px 32px rgba(31,38,135,0.08)', animationDelay: `${delay}s` }}>
    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(102,126,234,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 26, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 2 }}>{value}</div>
    <div style={{ fontSize: 11, color: '#8b8fa8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  card: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 24, padding: 24, marginBottom: 20, boxShadow: '0 8px 32px rgba(31,38,135,0.08)' },
  cardTitle: { fontSize: 16, fontWeight: 700, margin: '0 0 16px', color: '#1a1a2e' },
};

export default ComparativeAnalyticsPage;
